/**
 * Item Names — bulk upload from the Excel template (see BE src/masters/itemSheet.js).
 *
 * The sheet is read in the browser, previewed, then sent in chunks. Existing
 * names are skipped or updated (staff choose); blank cells never erase saved
 * values.
 */
import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useNotification } from "context/notification.context";
import { downloadItemTemplate, importItemMaster } from "network/service";

const CHUNK = 1000;

// Header text -> field (accepts the template's headers and close variants).
const FIELD_FOR = [
  ["name", /^(item\s*name|name|item|description)\b/i],
  ["hsn", /hsn/i],
  ["uom", /^(uom|unit)/i],
  ["rate", /rate|price/i],
  ["manufacturer", /manufacturer|brand/i],
];

/** Rows from the "Items" sheet (or the first sheet): [{ row, name, hsn, uom, rate, manufacturer }]. */
function readSheet(buffer) {
  const wb = XLSX.read(buffer, { type: "array" });
  const sheetName = wb.SheetNames.find((n) => n.trim().toLowerCase() === "items") || wb.SheetNames[0];
  const grid = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, raw: false, defval: "" });
  if (!grid.length) return { rows: [], missing: ["Item Name"] };
  const cols = {};
  grid[0].forEach((h, i) => {
    const hit = FIELD_FOR.find(([field, rx]) => !(field in cols) && rx.test(String(h).trim()));
    if (hit) cols[hit[0]] = i;
  });
  if (!("name" in cols)) return { rows: [], missing: ["Item Name"] };
  const rows = [];
  for (let r = 1; r < grid.length; r++) {
    const line = grid[r];
    if (!line.some((v) => String(v).trim())) continue; // blank row
    const item = { row: r + 1 };
    Object.entries(cols).forEach(([field, i]) => {
      item[field] = String(line[i] ?? "").trim();
    });
    rows.push(item);
  }
  return { rows, sheetName };
}

export default function ItemUpload({ onDone }) {
  const { showNotification } = useNotification();
  const fileRef = useRef(null);
  const [sheet, setSheet] = useState(null); // { file, rows, sheetName }
  const [mode, setMode] = useState("skip");
  const [progress, setProgress] = useState(null); // "1000 / 3200"
  const [result, setResult] = useState(null);

  const getTemplate = async () => {
    try {
      const resp = await downloadItemTemplate();
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "item-names-template.xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      showNotification("error", "Error", "Couldn't download the template");
    }
  };

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow picking the same file again
    if (!file) return;
    try {
      const { rows, missing, sheetName } = readSheet(await file.arrayBuffer());
      if (missing) return showNotification("error", "Wrong sheet", 'No "Item Name" column found — use the template');
      if (!rows.length) return showNotification("error", "Empty sheet", "No items found in the sheet");
      setResult(null);
      setSheet({ file: file.name, rows, sheetName });
    } catch (err) {
      showNotification("error", "Couldn't read the file", "Upload an Excel (.xlsx) or CSV file made from the template");
    }
  };

  const run = async () => {
    const total = sheet.rows.length;
    const sum = { created: 0, updated: 0, skipped: 0, unchanged: 0, duplicates: 0, errors: [], notes: [] };
    try {
      for (let i = 0; i < total; i += CHUNK) {
        setProgress(`${Math.min(i + CHUNK, total)} / ${total}`);
        // eslint-disable-next-line no-await-in-loop
        const { data } = await importItemMaster(sheet.rows.slice(i, i + CHUNK), mode);
        ["created", "updated", "skipped", "unchanged", "duplicates"].forEach((k) => { sum[k] += data[k] || 0; });
        sum.errors.push(...(data.errors || []));
        sum.notes.push(...(data.notes || []));
      }
      setResult(sum);
      showNotification("success", "Uploaded", `${sum.created} added, ${sum.updated} updated`);
      onDone && onDone();
    } catch (err) {
      setResult(sum);
      showNotification("error", "Upload stopped", err?.response?.data?.message || "Import failed part-way — the counts show what was saved");
      onDone && onDone();
    } finally {
      setProgress(null);
    }
  };

  const close = () => {
    if (progress) return;
    setSheet(null);
    setResult(null);
  };

  const noName = sheet ? sheet.rows.filter((r) => !r.name).length : 0;

  return (
    <>
      <MDButton variant="outlined" color="white" onClick={getTemplate}>
        <Icon>download</Icon>&nbsp;Template
      </MDButton>
      <MDButton variant="outlined" color="white" onClick={() => fileRef.current && fileRef.current.click()}>
        <Icon>upload_file</Icon>&nbsp;Upload sheet
      </MDButton>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={pick} />

      <Dialog open={!!sheet} onClose={close} maxWidth="md" fullWidth>
        {sheet && (
          <>
            <DialogTitle>Upload item names — {sheet.file}</DialogTitle>
            <DialogContent dividers>
              {!result ? (
                <>
                  <MDTypography variant="button" display="block">
                    {sheet.rows.length} row{sheet.rows.length === 1 ? "" : "s"} found in sheet &quot;{sheet.sheetName}&quot;
                    {noName ? ` · ${noName} without a name (will be skipped)` : ""}
                  </MDTypography>
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        {["Row", "Item Name", "HSN", "UOM", "Rate", "Manufacturer"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sheet.rows.slice(0, 8).map((r) => (
                        <TableRow key={r.row}>
                          <TableCell>{r.row}</TableCell>
                          <TableCell>{r.name || <em style={{ color: "#c62828" }}>missing</em>}</TableCell>
                          <TableCell>{r.hsn || "—"}</TableCell>
                          <TableCell>{r.uom || "—"}</TableCell>
                          <TableCell>{r.rate || "—"}</TableCell>
                          <TableCell>{r.manufacturer || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {sheet.rows.length > 8 ? (
                    <MDTypography variant="caption" color="text">…and {sheet.rows.length - 8} more</MDTypography>
                  ) : null}

                  <MDTypography variant="button" fontWeight="bold" display="block" mt={2}>
                    Items that already exist (same name, any case):
                  </MDTypography>
                  <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                    <FormControlLabel value="skip" control={<Radio />} label="Skip them — only add new names" />
                    <FormControlLabel
                      value="update"
                      control={<Radio />}
                      label="Update them with the sheet's HSN / UOM / rate / manufacturer (blank cells don't erase)"
                    />
                  </RadioGroup>
                </>
              ) : (
                <>
                  <MDTypography variant="button" display="block" fontWeight="bold">
                    {result.created} added · {result.updated} updated · {result.skipped} skipped (already there)
                    {result.unchanged ? ` · ${result.unchanged} unchanged` : ""}
                    {result.duplicates ? ` · ${result.duplicates} repeated in the sheet` : ""}
                  </MDTypography>
                  {result.errors.length ? (
                    <MDBox mt={1.5} p={1.5} sx={{ background: "#ffebee", border: "1px solid #e57373", borderRadius: 1.5 }}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: "#c62828" }}>
                        {result.errors.length} row{result.errors.length === 1 ? "" : "s"} not uploaded — fix and upload again:
                      </MDTypography>
                      {result.errors.slice(0, 50).map((e) => (
                        <MDTypography key={`${e.row}-${e.message}`} variant="caption" display="block">
                          Row {e.row}{e.name ? ` (${e.name})` : ""}: {e.message}
                        </MDTypography>
                      ))}
                      {result.errors.length > 50 ? <MDTypography variant="caption">…and {result.errors.length - 50} more</MDTypography> : null}
                    </MDBox>
                  ) : null}
                  {result.notes.length ? (
                    <MDBox mt={1.5} p={1.5} sx={{ background: "#fff8e1", border: "1px solid #ffca28", borderRadius: 1.5 }}>
                      <MDTypography variant="caption" fontWeight="bold" display="block">Adjusted while saving:</MDTypography>
                      {result.notes.slice(0, 30).map((n) => (
                        <MDTypography key={`${n.row}-${n.message}`} variant="caption" display="block">
                          Row {n.row}{n.name ? ` (${n.name})` : ""}: {n.message}
                        </MDTypography>
                      ))}
                      {result.notes.length > 30 ? <MDTypography variant="caption">…and {result.notes.length - 30} more</MDTypography> : null}
                    </MDBox>
                  ) : null}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <MDButton variant="text" color="secondary" onClick={close} disabled={!!progress}>
                {result ? "Close" : "Cancel"}
              </MDButton>
              {!result ? (
                <MDButton variant="gradient" color="info" onClick={run} disabled={!!progress}>
                  <Icon>upload</Icon>&nbsp;{progress ? `Uploading ${progress}…` : `Upload ${sheet.rows.length} rows`}
                </MDButton>
              ) : null}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
